import type { Kysely } from 'kysely';

import type { Database, FieldRow } from '../db/schema.js';
import { parseJson, stringifyJson } from '../db/values.js';
import { synthesiseFields } from '../validation/fields.js';

/**
 * Site-wide settings: what the site is called, what it falls back to, and how to reach it.
 *
 * Every one of these used to be a string a developer typed into the consumer's layout, which meant
 * an editor could not change the name of their own college without a deploy. That is the complaint
 * that moved branding, the accent and the AI settings into the admin, arriving one layer up.
 *
 * **The split between columns and JSON is the design.** What `resolveSeo` consumes is typed columns,
 * because core must never depend on a field `api_id` an admin can rename — the failure would be
 * silent, with SEO fallbacks simply stopping and nothing thrown to say so. What an institution wants
 * *beyond* that is different in every deployment, so it is defined by the admin and stored as JSON,
 * and the existing field system renders and validates it. There is no second field system.
 *
 * This module and `branding/branding.ts` write the same one row and name disjoint columns, which is
 * what makes two writers safe: neither upsert touches the other's values.
 */

/** The row id. There is only ever one; the check constraint in `0016_settings` says so too. */
const ROW_ID = 'site';

/** One social account. `platform` is a label, not an enum — see `SOCIAL_PRESETS`. */
export interface SocialProfile {
  platform: string;
  url: string;
}

export interface SiteSettings {
  name: string | null;
  titleTemplate: string | null;
  seoDescription: string | null;
  seoOgImageId: string | null;
  seoNoIndex: boolean;
  social: SocialProfile[];
  /** Definitions in `repeaterSubField`'s shape, ready for `synthesiseFields`. */
  extraFields: unknown[];
  /** One row of answers to those definitions, keyed by `api_id`. */
  extraValues: Record<string, unknown>;
}

/**
 * The platforms the screen offers by name, in the order it offers them.
 *
 * **Suggestions, not a vocabulary.** Storage is `[{ platform, url }]`, so an institution on
 * something nobody listed adds it by typing a name — the same arrangement the `embed` field's host
 * presets have, where the presets save typing and the allowlist is still whatever was chosen. A
 * fixed enum here would date the schema, and dating it would mean a migration to say a college is on
 * a new service.
 */
export const SOCIAL_PRESETS = [
  'Instagram',
  'Facebook',
  'X',
  'YouTube',
  'LinkedIn',
  'TikTok',
  'Bluesky',
  'Threads',
] as const;

export const MAX_SITE_NAME = 120;
export const MAX_TITLE_TEMPLATE = 200;
export const MAX_SEO_DESCRIPTION = 500;
/** Enough for every platform anybody lists and a bound on what the row will carry. */
export const MAX_SOCIAL_PROFILES = 20;

/** What every column reads as before anybody has been to the screen. */
export const EMPTY_SITE_SETTINGS: SiteSettings = {
  name: null,
  titleTemplate: null,
  seoDescription: null,
  seoOgImageId: null,
  seoNoIndex: false,
  social: [],
  extraFields: [],
  extraValues: {},
};

export async function getSiteSettings(db: Kysely<Database>): Promise<SiteSettings> {
  const row = await db
    .selectFrom('settings')
    .select([
      'site_name',
      'title_template',
      'seo_description',
      'seo_og_image_id',
      'seo_no_index',
      'social',
      'extra_fields',
      'extra_values',
    ])
    .where('id', '=', ROW_ID)
    .executeTakeFirst();

  if (!row) return EMPTY_SITE_SETTINGS;

  return {
    name: row.site_name ?? null,
    titleTemplate: row.title_template ?? null,
    seoDescription: row.seo_description ?? null,
    seoOgImageId: row.seo_og_image_id ?? null,
    seoNoIndex: Number(row.seo_no_index ?? 0) === 1,
    social: readSocial(row.social),
    // Tolerant on the way out as well as validated on the way in, because a row can be edited in a
    // database console and a settings screen that throws is one nobody can use to fix it.
    extraFields: Array.isArray(parseJson<unknown>(row.extra_fields, []))
      ? (parseJson<unknown[]>(row.extra_fields, []))
      : [],
    extraValues: parseJson<Record<string, unknown>>(row.extra_values, {}),
  };
}

/** The admin-defined fields as `FieldRow`s, ready for `FieldControl` and `validateItemData`. */
export function siteExtraFields(settings: SiteSettings): FieldRow[] {
  return synthesiseFields(settings.extraFields, {
    idPrefix: 'site',
    // These belong to no content type. The id is carried only so a synthesised row has the shape
    // every editing control expects; nothing joins on it.
    contentTypeId: ROW_ID,
    timestamp: '',
  });
}

export class SiteSettingsError extends Error {}

export interface SiteSettingsInput {
  name?: string | null;
  titleTemplate?: string | null;
  seoDescription?: string | null;
  seoOgImageId?: string | null;
  seoNoIndex?: boolean;
  social?: SocialProfile[];
  extraFields?: unknown[];
  extraValues?: Record<string, unknown>;
}

/**
 * Write the site-wide half of the one settings row.
 *
 * An upsert rather than a read-then-branch, for the reason `updateBranding` records: two requests
 * arriving together would both find no row and both insert, and the loser of that race is a
 * primary-key violation on a screen that was doing nothing unusual.
 *
 * Every key is optional and absent means "leave it", so a screen sending only what it renders cannot
 * blank what it does not — the same `undefined`/`null` distinction `updateItem` makes for
 * `publish_at`, and for the same reason: `??` collapses them and silently ignores a request to clear.
 */
export async function updateSiteSettings(
  db: Kysely<Database>,
  input: SiteSettingsInput,
  actorId: string | null,
): Promise<SiteSettings> {
  const current = await getSiteSettings(db);

  const next: SiteSettings = {
    name: pickText(input.name, current.name, MAX_SITE_NAME, 'Site name'),
    titleTemplate: pickText(
      input.titleTemplate,
      current.titleTemplate,
      MAX_TITLE_TEMPLATE,
      'Title template',
    ),
    seoDescription: pickText(
      input.seoDescription,
      current.seoDescription,
      MAX_SEO_DESCRIPTION,
      'Default description',
    ),
    seoOgImageId:
      input.seoOgImageId === undefined ? current.seoOgImageId : input.seoOgImageId?.trim() || null,
    seoNoIndex: input.seoNoIndex === undefined ? current.seoNoIndex : input.seoNoIndex,
    social: input.social === undefined ? current.social : normaliseSocial(input.social),
    extraFields: input.extraFields === undefined ? current.extraFields : input.extraFields,
    extraValues: input.extraValues === undefined ? current.extraValues : input.extraValues,
  };

  const values = {
    site_name: next.name,
    title_template: next.titleTemplate,
    seo_description: next.seoDescription,
    seo_og_image_id: next.seoOgImageId,
    seo_no_index: (next.seoNoIndex ? 1 : 0) as never,
    social: stringifyJson(next.social),
    extra_fields: stringifyJson(next.extraFields),
    extra_values: stringifyJson(next.extraValues),
    updated_at: new Date().toISOString(),
    updated_by: actorId,
  };

  await db
    .insertInto('settings')
    .values({ id: ROW_ID, ...values })
    .onConflict((conflict) => conflict.column('id').doUpdateSet(values))
    .execute();

  return next;
}

/**
 * A page's title with the site's template applied.
 *
 * Exported because both `resolveSeo` and the settings screen's live preview need it, and a second
 * copy of "where does `%s` go" is how a preview starts lying about what a visitor will see.
 *
 * A template with no `%s` is treated as having none at all rather than as a title of its own: an
 * admin halfway through typing one must not blank every page's title while they think about it.
 */
export function applyTitleTemplate(title: string, template: string | null | undefined): string {
  if (!template || !template.includes('%s')) return title;
  return template.replaceAll('%s', title);
}

function readSocial(stored: string | null): SocialProfile[] {
  const parsed = parseJson<unknown>(stored, []);
  if (!Array.isArray(parsed)) return [];

  return parsed.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const { platform, url } = entry as Record<string, unknown>;
    if (typeof platform !== 'string' || typeof url !== 'string') return [];
    if (!platform.trim() || !url.trim()) return [];
    return [{ platform: platform.trim(), url: url.trim() }];
  });
}

function normaliseSocial(input: SocialProfile[]): SocialProfile[] {
  const cleaned = input
    .map((entry) => ({ platform: String(entry.platform ?? '').trim(), url: String(entry.url ?? '').trim() }))
    // A row with only a platform is one somebody added and has not filled in; dropping it on save is
    // kinder than storing an account that points nowhere and rendering an icon linking to "".
    .filter((entry) => entry.platform && entry.url);

  if (cleaned.length > MAX_SOCIAL_PROFILES) {
    throw new SiteSettingsError(`No more than ${MAX_SOCIAL_PROFILES} social accounts.`);
  }

  return cleaned;
}

/** Blank clears; absent keeps; anything over the cap is refused rather than silently cut. */
function pickText(
  value: string | null | undefined,
  current: string | null,
  max: number,
  label: string,
): string | null {
  if (value === undefined) return current;
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return null;
  if (trimmed.length > max) {
    throw new SiteSettingsError(`${label} must be ${max} characters or fewer.`);
  }
  return trimmed;
}
